/**
 * Character roster screen (task 20.3 / task 18.1).
 *
 * Lists the signed-in player's roster and links to the guided creation form and
 * each character's sheet.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CharacterLedger } from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage } from "../api-client.js";

export function CharacterListScreen() {
  const { api } = useServices();
  const [roster, setRoster] = useState<readonly CharacterLedger[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .listCharacters()
      .then((r) => active && setRoster(r))
      .catch((e) => active && setError(friendlyErrorMessage(e, "Failed to load roster.")));
    return () => {
      active = false;
    };
  }, [api]);

  return (
    <section aria-label="Character roster">
      <h1>Your characters</h1>
      <Link to="/characters/new">Create a character</Link>
      {error ? <p role="alert">{error}</p> : null}
      {roster === null ? (
        <p>Loading roster…</p>
      ) : roster.length === 0 ? (
        <p>No characters yet.</p>
      ) : (
        <ul data-testid="roster">
          {roster.map((c) => (
            <li key={c.characterId}>
              <Link to={`/characters/${c.characterId}`}>{c.name}</Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
